import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "cityQuery",
    "cityStatus",
    "cityResults",
    "cityInputs",
    "selectedCities",
    "selectedCitiesEmpty",
    "neighborhoodQuery",
    "neighborhoodStatus",
    "neighborhoodResults",
    "neighborhoodInputs",
    "selectedNeighborhoods",
    "selectedNeighborhoodsEmpty",
    "neighborhoodHint"
  ]

  static values = {
    citiesUrl: String,
    neighborhoodsUrl: String,
    cityInputName: { type: String, default: "public_company_signup[city_ids][]" },
    neighborhoodInputName: { type: String, default: "public_company_signup[neighborhood_ids][]" },
    selectedCities: Array,
    selectedNeighborhoods: Array
  }

  connect() {
    this.selectedCitiesMap = new Map(
      (this.selectedCitiesValue || []).map((city) => [String(city.id), this.normalizeCity(city)])
    )

    this.selectedNeighborhoodsMap = new Map(
      (this.selectedNeighborhoodsValue || []).map((neighborhood) => [
        String(neighborhood.id),
        this.normalizeNeighborhood(neighborhood)
      ])
    )

    this.citySearchTimer = null
    this.neighborhoodSearchTimer = null
    this.cityAbortController = null
    this.neighborhoodAbortController = null
    this.latestCityResults = []
    this.latestNeighborhoodResults = []

    this.renderSelectedCities()
    this.renderSelectedNeighborhoods()
    this.renderCityInputs()
    this.renderNeighborhoodInputs()
    this.updateNeighborhoodAvailability()
    this.renderCityResults([])
    this.renderNeighborhoodResults([])

    if (this.selectedCitiesMap.size === 0) {
      this.setCityStatus("Digite pelo menos 2 letras para buscar a cidade.")
    } else {
      this.setCityStatus(`${this.selectedCitiesMap.size} cidade(s) selecionada(s).`)
    }
  }

  disconnect() {
    clearTimeout(this.citySearchTimer)
    clearTimeout(this.neighborhoodSearchTimer)
    this.cityAbortController?.abort()
    this.neighborhoodAbortController?.abort()
  }

  searchCities() {
    clearTimeout(this.citySearchTimer)

    const query = this.cityQueryTarget.value.toLowerCase().trim()
    if (query.length < 2) {
      this.latestCityResults = []
      this.renderCityResults([])
      this.setCityStatus("Digite pelo menos 2 letras para buscar a cidade.")
      return
    }

    this.setCityStatus("Buscando cidades...")
    this.citySearchTimer = window.setTimeout(() => this.loadCities(query), 220)
  }

  searchNeighborhoods() {
    clearTimeout(this.neighborhoodSearchTimer)

    if (this.selectedCityIdsWithNeighborhoods().length === 0) {
      this.updateNeighborhoodAvailability()
      return
    }

    const query = this.neighborhoodQueryTarget.value.toLowerCase().trim()
    if (query.length < 2) {
      this.latestNeighborhoodResults = []
      this.renderNeighborhoodResults([])
      this.setNeighborhoodStatus("Digite pelo menos 2 letras para buscar o bairro.")
      return
    }

    this.setNeighborhoodStatus("Buscando bairros...")
    this.neighborhoodSearchTimer = window.setTimeout(() => this.loadNeighborhoods(query), 220)
  }

  async loadCities(query) {
    this.cityAbortController?.abort()
    this.cityAbortController = new AbortController()

    try {
      const url = new URL(this.citiesUrlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.cityAbortController.signal
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const payload = await response.json()
      this.latestCityResults = Array.isArray(payload.items) ? payload.items.map((item) => this.normalizeCity(item)) : []
      this.renderCityResults(this.latestCityResults)

      if (this.latestCityResults.length === 0) {
        this.setCityStatus("Nenhuma cidade encontrada com esse termo.")
      } else {
        this.setCityStatus(`${this.latestCityResults.length} cidade(s) encontrada(s).`)
      }
    } catch (error) {
      if (error.name === "AbortError") return

      this.latestCityResults = []
      this.renderCityResults([])
      this.setCityStatus("Não foi possível buscar as cidades agora.")
    }
  }

  async loadNeighborhoods(query) {
    this.neighborhoodAbortController?.abort()
    this.neighborhoodAbortController = new AbortController()

    try {
      const url = new URL(this.neighborhoodsUrlValue, window.location.origin)
      url.searchParams.set("q", query)
      this.selectedCityIdsWithNeighborhoods().forEach((cityId) => url.searchParams.append("city_ids[]", cityId))

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        signal: this.neighborhoodAbortController.signal
      })

      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const payload = await response.json()
      this.latestNeighborhoodResults = Array.isArray(payload.items)
        ? payload.items.map((item) => this.normalizeNeighborhood(item))
        : []
      this.renderNeighborhoodResults(this.latestNeighborhoodResults)

      if (this.latestNeighborhoodResults.length === 0) {
        this.setNeighborhoodStatus("Nenhum bairro encontrado nas cidades selecionadas.")
      } else {
        this.setNeighborhoodStatus(`${this.latestNeighborhoodResults.length} bairro(s) encontrado(s).`)
      }
    } catch (error) {
      if (error.name === "AbortError") return

      this.latestNeighborhoodResults = []
      this.renderNeighborhoodResults([])
      this.setNeighborhoodStatus("Não foi possível buscar os bairros agora.")
    }
  }

  toggleCitySelection(city) {
    const cityId = String(city.id)

    if (this.selectedCitiesMap.has(cityId)) {
      this.selectedCitiesMap.delete(cityId)
      this.removeNeighborhoodsForCity(cityId)
    } else {
      this.selectedCitiesMap.set(cityId, this.normalizeCity(city))
    }

    this.removeNeighborhoodsWithoutCitySupport()
    this.renderSelectedCities()
    this.renderCityInputs()
    this.renderCityResults(this.latestCityResults)
    this.updateNeighborhoodAvailability()

    if (this.selectedCityIdsWithNeighborhoods().length === 0) {
      this.latestNeighborhoodResults = []
      this.renderNeighborhoodResults([])
      this.clearNeighborhoodSearch()
    } else if (this.neighborhoodQueryTarget.value.toLowerCase().trim().length >= 2) {
      this.searchNeighborhoods()
    } else {
      this.renderNeighborhoodResults([])
    }
  }

  toggleNeighborhoodSelection(neighborhood) {
    const neighborhoodId = String(neighborhood.id)

    if (this.selectedNeighborhoodsMap.has(neighborhoodId)) {
      this.selectedNeighborhoodsMap.delete(neighborhoodId)
    } else {
      this.selectedNeighborhoodsMap.set(neighborhoodId, this.normalizeNeighborhood(neighborhood))
    }

    this.renderSelectedNeighborhoods()
    this.renderNeighborhoodInputs()
    this.renderNeighborhoodResults(this.latestNeighborhoodResults)
  }

  removeSelectedCity(cityId) {
    this.selectedCitiesMap.delete(String(cityId))
    this.removeNeighborhoodsForCity(cityId)
    this.removeNeighborhoodsWithoutCitySupport()
    this.renderSelectedCities()
    this.renderCityInputs()
    this.renderCityResults(this.latestCityResults)
    this.updateNeighborhoodAvailability()

    if (this.selectedCityIdsWithNeighborhoods().length === 0) {
      this.latestNeighborhoodResults = []
      this.renderNeighborhoodResults([])
      this.clearNeighborhoodSearch()
    } else if (this.neighborhoodQueryTarget.value.toLowerCase().trim().length >= 2) {
      this.searchNeighborhoods()
    }
  }

  removeSelectedNeighborhood(neighborhoodId) {
    this.selectedNeighborhoodsMap.delete(String(neighborhoodId))
    this.renderSelectedNeighborhoods()
    this.renderNeighborhoodInputs()
    this.renderNeighborhoodResults(this.latestNeighborhoodResults)
  }

  renderCityResults(items) {
    if (!this.hasCityResultsTarget) return

    if (items.length === 0) {
      const helper = document.createElement("div")
      helper.className = "wizard-search-helper"
      helper.textContent = this.cityQueryTarget.value.trim().length >= 2
        ? "Nenhuma cidade encontrada para essa busca."
        : "Busque pela cidade ou estado para começar."

      this.cityResultsTarget.replaceChildren(helper)
      return
    }

    const row = document.createElement("div")
    row.className = "row g-3"

    items.forEach((city) => {
      const column = document.createElement("div")
      column.className = "col-12 col-md-6"

      const button = document.createElement("button")
      button.type = "button"
      button.className = this.selectedCitiesMap.has(String(city.id))
        ? "wizard-choice wizard-search-choice is-selected w-100 text-start"
        : "wizard-choice wizard-search-choice w-100 text-start"

      button.innerHTML = `
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div>
            <div class="wizard-choice-title">${city.name}</div>
            <div class="wizard-choice-meta">${city.state_name} · ${city.uses_neighborhoods ? "Permite bairros" : "Cidade inteira"}</div>
          </div>
          <span class="wizard-search-choice-badge">
            ${this.selectedCitiesMap.has(String(city.id)) ? "Selecionada" : "Adicionar"}
          </span>
        </div>
      `

      button.addEventListener("click", () => this.toggleCitySelection(city))
      column.appendChild(button)
      row.appendChild(column)
    })

    this.cityResultsTarget.replaceChildren(row)
  }

  renderNeighborhoodResults(items) {
    if (!this.hasNeighborhoodResultsTarget) return

    if (this.selectedCitiesMap.size === 0) {
      const helper = document.createElement("div")
      helper.className = "wizard-search-helper"
      helper.textContent = "Escolha pelo menos uma cidade para liberar a busca por bairros."
      this.neighborhoodResultsTarget.replaceChildren(helper)
      return
    }

    if (this.selectedCityIdsWithNeighborhoods().length === 0) {
      const helper = document.createElement("div")
      helper.className = "wizard-search-helper"
      helper.textContent = "As cidades selecionadas usam cobertura por cidade inteira. Não é necessário escolher bairros."
      this.neighborhoodResultsTarget.replaceChildren(helper)
      return
    }

    if (items.length === 0) {
      const helper = document.createElement("div")
      helper.className = "wizard-search-helper"
      helper.textContent = this.neighborhoodQueryTarget.value.trim().length >= 2
        ? "Nenhum bairro encontrado nas cidades escolhidas."
        : "Pesquise e selecione pelo menos um bairro para as cidades que usam cobertura detalhada."

      this.neighborhoodResultsTarget.replaceChildren(helper)
      return
    }

    const groupedItems = new Map()
    items.forEach((item) => {
      const cityKey = `${item.city_id}`
      const bucket = groupedItems.get(cityKey) || {
        cityId: item.city_id,
        cityName: item.city_name,
        stateName: item.state_name,
        items: []
      }

      bucket.items.push(item)
      groupedItems.set(cityKey, bucket)
    })

    const wrapper = document.createElement("div")
    wrapper.className = "d-grid gap-3"

    Array.from(groupedItems.values()).forEach((group) => {
      const groupCard = document.createElement("div")
      groupCard.className = "wizard-search-group"

      const header = document.createElement("div")
      header.className = "d-flex align-items-center justify-content-between gap-3 mb-3"
      header.innerHTML = `
        <div>
          <div class="fw-semibold text-dark">${group.cityName}</div>
          <div class="text-secondary small">${group.stateName}</div>
        </div>
        <span class="badge text-bg-light rounded-pill px-3 py-2">${group.items.length} opção(ões)</span>
      `

      const row = document.createElement("div")
      row.className = "row g-3"

      group.items.forEach((neighborhood) => {
        const column = document.createElement("div")
        column.className = "col-12 col-md-6"

        const button = document.createElement("button")
        button.type = "button"
        button.className = this.selectedNeighborhoodsMap.has(String(neighborhood.id))
          ? "wizard-choice wizard-search-choice is-selected w-100 text-start"
          : "wizard-choice wizard-search-choice w-100 text-start"

        button.innerHTML = `
          <div class="d-flex align-items-start justify-content-between gap-3">
            <div>
              <div class="wizard-choice-title">${neighborhood.name}</div>
              <div class="wizard-choice-meta">${neighborhood.city_name} · ${neighborhood.state_name}</div>
            </div>
            <span class="wizard-search-choice-badge">
              ${this.selectedNeighborhoodsMap.has(String(neighborhood.id)) ? "Selecionado" : "Adicionar"}
            </span>
          </div>
        `

        button.addEventListener("click", () => this.toggleNeighborhoodSelection(neighborhood))
        column.appendChild(button)
        row.appendChild(column)
      })

      groupCard.appendChild(header)
      groupCard.appendChild(row)
      wrapper.appendChild(groupCard)
    })

    this.neighborhoodResultsTarget.replaceChildren(wrapper)
  }

  renderSelectedCities() {
    if (!this.hasSelectedCitiesTarget || !this.hasSelectedCitiesEmptyTarget) return

    this.selectedCitiesTarget.innerHTML = ""
    const cities = Array.from(this.selectedCitiesMap.values()).sort((left, right) => {
      return left.name.localeCompare(right.name, "pt-BR")
    })

    cities.forEach((city) => {
      const chip = document.createElement("button")
      chip.type = "button"
      chip.className = "wizard-selected-chip"
      chip.innerHTML = `
        <span>${city.name} · ${city.state_name}${city.uses_neighborhoods ? "" : " · cidade inteira"}</span>
        <span class="wizard-selected-chip-remove"><i class="bi bi-x-lg"></i></span>
      `
      chip.addEventListener("click", () => this.removeSelectedCity(city.id))
      this.selectedCitiesTarget.appendChild(chip)
    })

    this.selectedCitiesEmptyTarget.classList.toggle("d-none", cities.length > 0)
    this.setCityStatus(`${cities.length} cidade(s) selecionada(s).`)
  }

  renderSelectedNeighborhoods() {
    if (!this.hasSelectedNeighborhoodsTarget || !this.hasSelectedNeighborhoodsEmptyTarget) return

    this.selectedNeighborhoodsTarget.innerHTML = ""
    const neighborhoods = Array.from(this.selectedNeighborhoodsMap.values()).sort((left, right) => {
      return left.name.localeCompare(right.name, "pt-BR")
    })

    neighborhoods.forEach((neighborhood) => {
      const chip = document.createElement("button")
      chip.type = "button"
      chip.className = "wizard-selected-chip wizard-selected-chip-dark"
      chip.innerHTML = `
        <span>${neighborhood.name} · ${neighborhood.city_name}</span>
        <span class="wizard-selected-chip-remove"><i class="bi bi-x-lg"></i></span>
      `
      chip.addEventListener("click", () => this.removeSelectedNeighborhood(neighborhood.id))
      this.selectedNeighborhoodsTarget.appendChild(chip)
    })

    this.selectedNeighborhoodsEmptyTarget.classList.toggle("d-none", neighborhoods.length > 0)
    this.setNeighborhoodStatus(`${neighborhoods.length} bairro(s) selecionado(s).`)
  }

  renderCityInputs() {
    if (!this.hasCityInputsTarget) return

    this.cityInputsTarget.innerHTML = ""
    this.selectedCityIds().forEach((cityId) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.cityInputNameValue
      input.value = cityId
      this.cityInputsTarget.appendChild(input)
    })
  }

  renderNeighborhoodInputs() {
    if (!this.hasNeighborhoodInputsTarget) return

    this.neighborhoodInputsTarget.innerHTML = ""
    Array.from(this.selectedNeighborhoodsMap.keys()).forEach((neighborhoodId) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = this.neighborhoodInputNameValue
      input.value = neighborhoodId
      this.neighborhoodInputsTarget.appendChild(input)
    })
  }

  updateNeighborhoodAvailability() {
    if (!this.hasNeighborhoodQueryTarget || !this.hasNeighborhoodHintTarget) return

    const hasCities = this.selectedCitiesMap.size > 0
    const hasCitiesWithNeighborhoods = this.selectedCityIdsWithNeighborhoods().length > 0
    this.neighborhoodQueryTarget.disabled = !hasCitiesWithNeighborhoods

    if (!hasCities) {
      this.neighborhoodHintTarget.textContent = "Selecione uma ou mais cidades para liberar a busca por bairros."
      this.setNeighborhoodStatus("Os bairros aparecem depois que pelo menos uma cidade é escolhida.")
      return
    }

    if (!hasCitiesWithNeighborhoods) {
      this.neighborhoodHintTarget.textContent = "As cidades selecionadas usam cobertura por cidade inteira. O bairro não será solicitado."
      this.setNeighborhoodStatus("Nenhum bairro necessário para as cidades escolhidas.")
      return
    }

    this.neighborhoodHintTarget.textContent = "Selecione pelo menos um bairro para cada cidade que usa cobertura detalhada por bairros."
    this.setNeighborhoodStatus(`${this.selectedNeighborhoodsMap.size} bairro(s) selecionado(s).`)
  }

  clearNeighborhoodSearch() {
    if (!this.hasNeighborhoodQueryTarget) return

    this.neighborhoodQueryTarget.value = ""
    if (this.selectedCitiesMap.size === 0) {
      this.setNeighborhoodStatus("Os bairros aparecem depois que pelo menos uma cidade é escolhida.")
    } else if (this.selectedCityIdsWithNeighborhoods().length === 0) {
      this.setNeighborhoodStatus("Nenhum bairro necessário para as cidades escolhidas.")
    }
  }

  removeNeighborhoodsForCity(cityId) {
    Array.from(this.selectedNeighborhoodsMap.entries()).forEach(([id, neighborhood]) => {
      if (String(neighborhood.city_id) === String(cityId)) {
        this.selectedNeighborhoodsMap.delete(id)
      }
    })

    this.renderSelectedNeighborhoods()
    this.renderNeighborhoodInputs()
  }

  removeNeighborhoodsWithoutCitySupport() {
    const cityIdsWithNeighborhoods = new Set(this.selectedCityIdsWithNeighborhoods())

    Array.from(this.selectedNeighborhoodsMap.entries()).forEach(([id, neighborhood]) => {
      if (!cityIdsWithNeighborhoods.has(String(neighborhood.city_id))) {
        this.selectedNeighborhoodsMap.delete(id)
      }
    })

    this.renderSelectedNeighborhoods()
    this.renderNeighborhoodInputs()
  }

  selectedCityIds() {
    return Array.from(this.selectedCitiesMap.keys())
  }

  selectedCityIdsWithNeighborhoods() {
    return Array
      .from(this.selectedCitiesMap.values())
      .filter((city) => city.uses_neighborhoods)
      .map((city) => String(city.id))
  }

  setCityStatus(message) {
    if (this.hasCityStatusTarget) {
      this.cityStatusTarget.textContent = message
    }
  }

  setNeighborhoodStatus(message) {
    if (this.hasNeighborhoodStatusTarget) {
      this.neighborhoodStatusTarget.textContent = message
    }
  }

  normalizeCity(city) {
    return {
      id: Number(city.id),
      name: city.name,
      state_name: city.state_name,
      uses_neighborhoods: city.uses_neighborhoods !== false
    }
  }

  normalizeNeighborhood(neighborhood) {
    return {
      id: Number(neighborhood.id),
      name: neighborhood.name,
      city_id: Number(neighborhood.city_id),
      city_name: neighborhood.city_name,
      state_name: neighborhood.state_name
    }
  }
}
